import styled from 'styled-components';

import { Button } from '@components/button';
import { useFormContext } from '@components/form';
import { useFormField } from '@components/form/use-form-field';
import { IconListDynamic } from '@components/icons/list-dynamic';
import { hideModal, Modal, useModal } from '@components/modal';
import { Select } from '@components/select';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { getParameterMapFieldId } from './endpoint-fields';
import type {
  IntegrationEndpointListVariable,
  IntegrationEndpointVariable,
  SyncListParameter,
} from './integrations-types';

type Props = {
  field: IntegrationEndpointListVariable;
  allFields: IntegrationEndpointVariable[];
  parameters: SyncListParameter[];
  keepOpen: () => void;
};

// Registers a form field per parameter, outside the Collapse so closing
// the drawer keeps the mapping.
export const HiddenParameterMapFields = ({
  field,
  parameters,
}: Omit<Props, 'allFields' | 'keepOpen'>) => {
  return (
    <>
      {parameters.map((parameter) => (
        <HiddenParameterField
          key={parameter.name}
          fieldId={getParameterMapFieldId(field.name, parameter.name)}
          defaultValue={field.parameter_map?.[parameter.name] ?? null}
        />
      ))}
    </>
  );
};

export const DynamicListParameterMap = ({ field, allFields, parameters, keepOpen }: Props) => {
  const { t } = useScopedTranslate(locale);
  const { store } = useFormContext();
  const formValues = store.useFormValues();
  const showModal = useModal(ParameterMapModal);

  if (parameters.length === 0) return null;

  const currentMap: Record<string, string> = {};
  for (const parameter of parameters) {
    const value = formValues[getParameterMapFieldId(field.name, parameter.name)];
    if (typeof value === 'string' && value) {
      currentMap[parameter.name] = value;
    }
  }
  const filledCount = Object.keys(currentMap).length;
  const isComplete = filledCount === parameters.length;

  const variableOptions = allFields
    .filter((endpointVariable) => endpointVariable.name !== field.name)
    .map((endpointVariable) => ({
      value: endpointVariable.name,
      label: endpointVariable.label || endpointVariable.name,
    }));

  const handleClick = () => {
    // Keep the drawer open while mapping so it doesn't auto-collapse the
    // moment the last parameter is filled (which would unmount the modal trigger).
    keepOpen();
    showModal({
      variableLabel: field.label || field.name,
      parameters,
      variableOptions,
      currentMap,
      onChange: (parameterName, value) => {
        store.actions.setValue(getParameterMapFieldId(field.name, parameterName), value || null);
      },
    });
  };

  const color = isComplete ? 'elementPurple100' : 'elementRed100';
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleClick}
      color={color}
      iconLeft={<IconListDynamic size={16} color={color} />}
      text={t('status', { filled: filledCount, total: parameters.length })}
      // marginLeft lines up with the list input: 24px arrow + 16px row gap.
      style={{ alignSelf: 'flex-start', marginLeft: pxToRem(40), marginTop: pxToRem(8) }}
    />
  );
};

const HiddenParameterField = ({
  fieldId,
  defaultValue,
}: {
  fieldId: string;
  defaultValue: string | null;
}) => {
  useFormField({ id: fieldId, label: '', defaultValue });
  return null;
};

type FieldOption = { value: string; label: string };

type ParameterMapModalProps = {
  variableLabel: string;
  parameters: SyncListParameter[];
  variableOptions: FieldOption[];
  currentMap: Record<string, string>;
  onChange: (parameter: string, value: string) => void;
};

const ParameterMapModal = ({
  variableLabel,
  parameters,
  variableOptions,
  currentMap,
  onChange,
}: ParameterMapModalProps) => {
  const { t } = useScopedTranslate(locale);

  return (
    <Modal>
      <Modal.SimpleHeader title={t('modalTitle', { variable: variableLabel })} />
      <Modal.Content>
        <Stack space={4}>
          <Text text={t('modalDescription')} font="regular14" color="textSecondary" />
          <Stack space={2}>
            {parameters.map((parameter) => (
              <Row key={parameter.name}>
                <Text text={parameter.label} font="regular14" color="textHelper" />
                <Select
                  label={parameter.label}
                  hideLabel
                  options={variableOptions}
                  value={currentMap[parameter.name] || undefined}
                  onChange={(value) => onChange(parameter.name, (value as string) ?? '')}
                  placeholder={t('selectField')}
                />
              </Row>
            ))}
          </Stack>
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <Stack orientation="horizontal" justify="flex-end">
          <Button variant="secondary" onClick={hideModal} text={t('close')} />
        </Stack>
      </Modal.Footer>
    </Modal>
  );
};

const Row = styled.div({
  display: 'grid',
  gridTemplateColumns: `${pxToRem(140)} 1fr`,
  alignItems: 'center',
  gap: pxToRem(12),
});

const locale = {
  en: {
    status: '{filled}/{total} parameters mapped',
    modalTitle: 'Map parameters for {variable}',
    modalDescription:
      "This list's options depend on values from other endpoint variables. Choose which variable provides each parameter so the list can load.",
    selectField: 'Select field',
    close: 'Close',
  },
};
